import { MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import React, { useMemo } from 'react';

export interface DescriptiveSelectOption {
  // The underlying value for the option, usually a string or number.
  value: string | number;

  // The viewable label for the option, usually a translation string.
  label: string;

  // Will be displayed below the label to describe the option, usually a translation string.
  description?: string | React.ReactNode;

  // An optional (rarely needed) string to display for the selected value to use in place of the label,
  // or a function that takes the value and returns a React node to render the selected value.
  renderValue?: string | ((value: string | number) => React.ReactNode);
}

export interface DescriptiveSelectProps {
  fullWidth?: boolean;
  value: string | number;
  onChange: (value: string | number | null) => void;

  options: Array<DescriptiveSelectOption>;
}

export function DescriptiveSelect(props: DescriptiveSelectProps): JSX.Element {
  const { value, onChange, options } = props;

  const selectedOption = useMemo(() => {
    return options.find(option => option.value === value);
  }, [value, options]);

  return (
    <Select
      fullWidth={props.fullWidth}
      variant="outlined"
      displayEmpty
      value={value || ""}
      renderValue={(value) => {
        if (selectedOption?.renderValue) {
          if (typeof selectedOption.renderValue === "function") {
            return selectedOption.renderValue(value);
          } else {
            return selectedOption.renderValue;
          }
        } else {
          return selectedOption?.label || "";
        }
      }}
      onChange={(event: SelectChangeEvent<string | number>) =>  {
        onChange(event.target.value || null);
      }} >
      {options.map((option) => (
        <MenuItem key={option.value} divider value={option.value} >
          <Typography sx={{maxWidth:"450px", display:"block", whiteSpace:"normal", wordWrap:"break-word"}}>
            <strong>{option.label}</strong><br/>
            {option.description}
          </Typography>
        </MenuItem>
      ))}
    </Select>
  );
}